/**
 * Servicio de diagramas para el chatbot del libro
 * Maneja la generación y renderizado de diagramas Mermaid y mapas mentales Mind Elixir
 */

import mermaid from 'mermaid';

// Inicializar Mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'sans-serif',
});

/**
 * Detecta el tipo de diagrama solicitado
 * @param {string} query - Consulta del usuario
 * @returns {'mindmap' | 'mermaid-flowchart' | 'mermaid-sequence' | 'mermaid-class' | 'image' | null}
 */
export function detectDiagramType(query) {
  const queryLower = query.toLowerCase();
  
  // Mapas mentales (Mind Elixir)
  const mindmapKeywords = ['mapa mental', 'mind map', 'mapa de ideas', 'resumen visual', 'mapa conceptual'];
  if (mindmapKeywords.some(kw => queryLower.includes(kw))) {
    return 'mindmap';
  }
  
  // Diagrama de flujo (Mermaid)
  const flowchartKeywords = ['diagrama de flujo', 'flowchart', 'proceso', 'pasos', 'flujo de'];
  if (flowchartKeywords.some(kw => queryLower.includes(kw))) {
    return 'mermaid-flowchart';
  }
  
  // Diagrama de secuencia (Mermaid)
  const sequenceKeywords = ['diagrama de secuencia', 'sequence diagram', 'interacción', 'comunicación entre'];
  if (sequenceKeywords.some(kw => queryLower.includes(kw))) {
    return 'mermaid-sequence';
  }
  
  // Diagrama de clases/estructura (Mermaid)
  const classKeywords = ['diagrama de clases', 'class diagram', 'estructura', 'jerarquía', 'organigrama'];
  if (classKeywords.some(kw => queryLower.includes(kw))) {
    return 'mermaid-class';
  }
  
  // Diagrama genérico (Mermaid flowchart por defecto)
  const genericDiagramKeywords = ['diagrama', 'esquema'];
  if (genericDiagramKeywords.some(kw => queryLower.includes(kw))) {
    return 'mermaid-flowchart';
  }
  
  // Si pide imagen explícita -> usar DALL-E
  const imageKeywords = ['crea una imagen', 'genera una imagen', 'dibuja', 'infografía'];
  if (imageKeywords.some(kw => queryLower.includes(kw))) {
    return 'image';
  }
  
  return null;
}

/**
 * Genera el prompt para que OpenAI cree el código del diagrama
 * @param {string} diagramType - Tipo de diagrama
 * @param {string} userQuery - Consulta del usuario
 * @param {string} bookContent - Contenido relevante del libro
 * @returns {string} Prompt para OpenAI
 */
export function generateDiagramPrompt(diagramType, userQuery, bookContent) {
  const baseInstructions = `
CONTENIDO DEL LIBRO: ${bookContent}
SOLICITUD: ${userQuery}

REGLAS CRÍTICAS:
- Responde ÚNICAMENTE con el código, sin explicaciones
- NO uses bloques markdown (\`\`\`)
- Contenido en español
`;

  switch (diagramType) {
    case 'mindmap':
      return `Genera un mapa mental JSON para Mind Elixir con estructura RAMIFICADA.

FORMATO JSON REQUERIDO:
{"nodeData":{"id":"root","topic":"TÍTULO","children":[...]}}

ESTRUCTURA JERÁRQUICA:
- Raíz: Tema principal del contenido
- Nivel 1: Temas o categorías principales (children del root)
- Nivel 2: Subtemas de cada tema (children de nivel 1)
- Nivel 3: Conceptos específicos si el contenido lo amerita

REGLAS DE NODOS:
- id: string único ("1", "1-1", "1-2", "2", "2-1")
- topic: texto conciso 3-8 palabras
- children: array de nodos hijos ([] si no tiene)

IMPORTANTE:
- Mínimo 3 ramas principales desde la raíz
- Cada rama debe tener al menos 2 subtemas si el contenido lo permite
- Organiza por temas/categorías, no de forma lineal
- Solo devuelve JSON valido, sin texto adicional
- No cierres arrays antes de tiempo.
${baseInstructions}`;

    case 'mermaid-flowchart':
      return `Genera un diagrama Mermaid con estructura RAMIFICADA tipo árbol.

SINTAXIS:
flowchart TD
    A[Nodo Central] --> B[Rama 1]
    A --> C[Rama 2]
    B --> B1[Subnodo]
    B --> B2[Subnodo]

ESTRUCTURA:
- Un nodo central conecta a múltiples ramas
- Cada rama puede tener sus propios subnodos
- Forma de árbol, NO lineal

REGLAS:
- IDs: letras simples (A, B, C, B1, B2)
- Texto entre corchetes [Texto del nodo]
- Conexiones con -->
${baseInstructions}`;

    case 'mermaid-sequence':
      return `Genera un diagrama de secuencia Mermaid.

SINTAXIS:
sequenceDiagram
    participant A as Nombre1
    participant B as Nombre2
    A->>B: Mensaje
    B-->>A: Respuesta

REGLAS:
- Mensajes claros y concisos
${baseInstructions}`;

    case 'mermaid-class':
      return `Genera un diagrama jerárquico Mermaid con subgraphs.

SINTAXIS:
flowchart TB
    subgraph Grupo1[Título Grupo]
        A[Nodo] --> B[Nodo]
    end
    subgraph Grupo2[Título Grupo]
        C[Nodo]
    end
    Grupo1 --> Grupo2

ESTRUCTURA:
- Usa subgraph para agrupar temas relacionados
- Conecta grupos entre sí para mostrar relaciones
${baseInstructions}`;

    default:
      return `Genera un diagrama Mermaid ramificado.

flowchart TD
    A[Central] --> B[Tema1]
    A --> C[Tema2]
    B --> B1[Sub1]

Estructura de árbol.
${baseInstructions}`;
  }
}

/**
 * Limpia el código generado por la IA
 * @param {string} code - Código generado
 * @returns {string} Código limpio
 */
export function cleanGeneratedCode(code) {
  let cleaned = code.trim();

  // Remover bloques de código markdown
  cleaned = cleaned.replace(/```mermaid\n?/gi, '');
  cleaned = cleaned.replace(/```markdown\n?/gi, '');
  cleaned = cleaned.replace(/```\n?/g, '');

  // Remover líneas vacías al inicio y final
  cleaned = cleaned.trim();

  return cleaned;
}

/**
 * Sanitiza etiquetas de nodos Mermaid para evitar errores de parseo.
 * Las comillas " dentro de corchetes [] () {} rompen el parser de Mermaid.
 */
function sanitizeMermaidLabels(code) {
  return code
    .split('\n')
    .map(line => {
      // Reemplaza comillas (rectas y tipográficas) dentro de etiquetas de nodos: [..] (..) {..}
      return line.replace(
        /(\[|\()([^\]\)\n]+)(\]|\))/g,
        (_match, open, content, close) => {
          const clean = content
            .replace(/["""]/g, "'")  // comillas rectas y tipográficas → comilla simple
            .replace(/[''']/g, "'"); // comillas tipográficas simples normalizadas
          return `${open}${clean}${close}`;
        }
      ).replace(
        /\{([^}\n]+)\}/g,
        (_match, content) => {
          const clean = content
            .replace(/["""]/g, "'")
            .replace(/[''']/g, "'");
          return `{${clean}}`;
        }
      );
    })
    .join('\n');
}

/**
 * Renderiza código Mermaid a SVG
 * @param {string} code - Código Mermaid
 * @returns {Promise<{svg: string, error: string | null}>}
 */
export async function renderMermaid(code) {
  try {
    const cleanCode = sanitizeMermaidLabels(cleanGeneratedCode(code));
    const id = `mermaid-${Date.now()}`;
    
    // Validar que el código tenga una directiva válida
    const validDirectives = ['flowchart', 'sequenceDiagram', 'classDiagram', 'graph', 'stateDiagram', 'erDiagram', 'gantt', 'pie'];
    const hasValidDirective = validDirectives.some(d => cleanCode.toLowerCase().startsWith(d.toLowerCase()));
    
    if (!hasValidDirective) {
      return { svg: null, error: 'Código Mermaid inválido: falta directiva inicial' };
    }
    
    const { svg } = await mermaid.render(id, cleanCode);
    return { svg, error: null };
  } catch (error) {
    console.error('[DiagramService] Error renderizando Mermaid:', error);
    return { svg: null, error: error.message || 'Error al renderizar el diagrama' };
  }
}

/**
 * Repara JSON generado por la IA recorriendo carácter por carácter.
 * Elimina llaves/corchetes sobrantes (ej: ]}} donde debería ser ]}),
 * trailing commas, saltos de línea dentro de strings y comillas tipográficas.
 */
function repairJson(raw) {
  let s = cleanGeneratedCode(raw);

  s = s.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");

  const start = s.indexOf('{');
  const end = s.lastIndexOf('}');
  if (start !== -1 && end !== -1 && end > start) s = s.slice(start, end + 1);

  let result = '';
  let inString = false;
  let escaped = false;
  const stack = [];
  let rootClosed = false;

  for (let i = 0; i < s.length; i++) {
    // En cuanto el objeto raíz esté cerrado, parar — evita contenido extra tras el JSON
    if (rootClosed) break;

    const ch = s[i];
    if (escaped) { result += ch; escaped = false; continue; }
    if (ch === '\\' && inString) { result += ch; escaped = true; continue; }
    if (ch === '"') { inString = !inString; result += ch; continue; }
    if (inString) {
      result += (ch === '\n' || ch === '\r') ? ' ' : ch;
      continue;
    }
    if (ch === '{' || ch === '[') {
      stack.push(ch); result += ch;
    } else if (ch === '}') {
      if (stack.length && stack[stack.length - 1] === '{') {
        stack.pop(); result += ch;
        if (stack.length === 0) rootClosed = true;
      }
    } else if (ch === ']') {
      if (stack.length && stack[stack.length - 1] === '[') {
        stack.pop(); result += ch;
        if (stack.length === 0) rootClosed = true;
      }
    } else {
      result += ch;
    }
  }

  // Solo cerrar estructuras pendientes si el root nunca se cerró limpiamente
  if (!rootClosed) {
    while (stack.length) result += stack.pop() === '{' ? '}' : ']';
  }

  // Eliminar trailing commas antes de ] o }
  result = result.replace(/,(\s*[\]}])/g, '$1');

  return result;
}

/**
 * Parsea JSON de Mind Elixir
 * @param {string} jsonString - JSON generado por la IA
 * @returns {{data: object, error: string | null}}
 */
export function parseMindElixirData(jsonString) {
  try {
    let cleanJson = repairJson(jsonString);

    // Segunda defensa: si aún falla el parse, intentar con el primer bloque JSON válido
    let data;
    try {
      data = JSON.parse(cleanJson);
    } catch {
      // Buscar el primer objeto JSON completo buscando llaves balanceadas
      let depth = 0, start = -1, end = -1;
      for (let i = 0; i < cleanJson.length; i++) {
        if (cleanJson[i] === '{') { if (depth === 0) start = i; depth++; }
        else if (cleanJson[i] === '}') { depth--; if (depth === 0 && start !== -1) { end = i; break; } }
      }
      if (start !== -1 && end !== -1) {
        data = JSON.parse(cleanJson.slice(start, end + 1));
      } else {
        throw new Error('No se encontró un objeto JSON válido');
      }
    }

    // Validar estructura básica
    if (!data.nodeData || !data.nodeData.topic) {
      return { data: null, error: 'Estructura JSON inválida: falta nodeData.topic' };
    }

    // Asegurar que tenga id en el root
    if (!data.nodeData.id) {
      data.nodeData.id = 'root';
    }

    // Asignar IDs a nodos que no tengan
    assignNodeIds(data.nodeData, 'root');

    return { data, error: null };
  } catch (error) {
    console.error('[DiagramService] Error parseando Mind Elixir JSON:', error);
    return { data: null, error: error.message || 'Error al procesar el mapa mental' };
  }
}

/**
 * Asigna IDs únicos a nodos que no tengan
 * @param {object} node - Nodo a procesar
 * @param {string} prefix - Prefijo para el ID
 */
function assignNodeIds(node, prefix) {
  if (!node.id) {
    node.id = prefix;
  }
  if (node.children && Array.isArray(node.children)) {
    node.children.forEach((child, index) => {
      assignNodeIds(child, `${prefix}-${index + 1}`);
    });
  }
}

/**
 * Exporta un elemento SVG a imagen descargable
 * @param {SVGElement} svgElement - Elemento SVG
 * @param {string} filename - Nombre del archivo
 * @param {'png' | 'svg'} format - Formato de exportación
 * @param {Object} options - Opciones adicionales
 * @param {string} options.backgroundColor - Color de fondo (default: #1a1a2e para markmap)
 */
export async function exportDiagramToImage(svgElement, filename = 'diagrama', format = 'png', options = {}) {
  try {
    // Clonar el SVG para no modificar el original
    const svgClone = svgElement.cloneNode(true);
    
    // Detectar si es un mapa mental
    const isMindmap = filename.includes('mapa_mental');
    
    // Colores para exportación (siempre modo claro)
    const backgroundColor = options.backgroundColor || '#ffffff';
    
    // Obtener dimensiones reales del contenido
    let width, height;
    try {
      const bbox = svgElement.getBBox();
      // Agregar padding
      width = Math.max(bbox.width + bbox.x + 100, 800);
      height = Math.max(bbox.height + bbox.y + 100, 600);
    } catch (e) {
      width = parseInt(svgElement.getAttribute('width')) || 800;
      height = parseInt(svgElement.getAttribute('height')) || 600;
    }
    
    // Configurar el SVG clonado con dimensiones fijas y viewBox
    svgClone.setAttribute('width', width);
    svgClone.setAttribute('height', height);
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    
    // Agregar un rectángulo de fondo al SVG
    const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bgRect.setAttribute('width', '100%');
    bgRect.setAttribute('height', '100%');
    bgRect.setAttribute('fill', backgroundColor);
    svgClone.insertBefore(bgRect, svgClone.firstChild);
    
    // Asegurar que los textos tengan el color correcto
    if (isMindmap) {
      const texts = svgClone.querySelectorAll('text');
      texts.forEach(text => {
        text.setAttribute('fill', '#000000');
      });
    }
    
    const svgData = new XMLSerializer().serializeToString(svgClone);
    
    if (format === 'svg') {
      // Exportar como SVG
      const blob = new Blob([svgData], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      downloadFile(url, `${filename}.svg`);
      URL.revokeObjectURL(url);
    } else {
      // Exportar como PNG
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      const scale = 2; // 2x para mejor calidad
      canvas.width = width * scale;
      canvas.height = height * scale;
      
      return new Promise((resolve, reject) => {
        img.onload = () => {
          // Dibujar fondo
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Dibujar imagen
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            downloadFile(url, `${filename}.png`);
            URL.revokeObjectURL(url);
            resolve();
          }, 'image/png');
        };
        
        img.onerror = (e) => {
          console.error('[DiagramService] Error cargando imagen SVG:', e);
          reject(e);
        };
        
        // Usar base64 para evitar problemas de CORS
        const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
        img.src = 'data:image/svg+xml;base64,' + svgBase64;
      });
    }
  } catch (error) {
    console.error('[DiagramService] Error exportando diagrama:', error);
    throw error;
  }
}

/**
 * Descarga un archivo
 * @param {string} url - URL del archivo
 * @param {string} filename - Nombre del archivo
 */
function downloadFile(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Copia el código del diagrama al portapapeles
 * @param {string} code - Código a copiar
 * @returns {Promise<boolean>}
 */
export async function copyCodeToClipboard(code) {
  try {
    await navigator.clipboard.writeText(code);
    return true;
  } catch (error) {
    console.error('[DiagramService] Error copiando al portapapeles:', error);
    return false;
  }
}

export default {
  detectDiagramType,
  generateDiagramPrompt,
  cleanGeneratedCode,
  renderMermaid,
  parseMindElixirData,
  exportDiagramToImage,
  copyCodeToClipboard
};
